import request from './request';

export interface DashboardStats {
  totalUsers: number;
  todayLogs: number;
  totalSessions: number;
  activeUsers: number;
}

export interface DailyCount {
  date: string;
  count: number;
}

export interface DashboardTrend {
  dailyUsers: DailyCount[];
  dailyLogs: DailyCount[];
  dailySessions: DailyCount[];
}

export interface AdminUser {
  id: string;
  email: string;
  phone: string;
  nickname: string;
  avatarUrl: string;
  role: string;
  status: string;
  lastLoginAt: string;
  lastSessionAt: string;
  createdAt: string;
}

export interface UserProfile {
  heightCm: number | null;
  weightKg: number | null;
  age: number | null;
  gender: string | null;
  targetWeightKg: number | null;
  activityLevel: string | null;
  dailyCalorieTarget: number | null;
  proteinTarget: number | null;
  fatTarget: number | null;
  carbsTarget: number | null;
}

export interface CalorieStats {
  totalLogs: number;
  totalCalories: number | null;
  todayCalories: number | null;
  avgDailyCalories: number | null;
}

export interface UserDetail {
  id: string;
  email: string;
  phone: string;
  nickname: string;
  avatarUrl: string;
  role: string;
  status: string;
  lastLoginAt: string;
  lastSessionAt: string;
  createdAt: string;
  profile: UserProfile | null;
  calorieStats: CalorieStats | null;
}

export interface VoiceSession {
  id: string;
  status: string;
  transcriptText: string;
  clarificationRound: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  items: T[];
}

export function getDashboard(): Promise<DashboardStats> {
  return request.get('/api/admin/v1/dashboard');
}

export function getDashboardTrend(): Promise<DashboardTrend> {
  return request.get('/api/admin/v1/dashboard/trend');
}

export function getUsers(params: {
  keyword?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<AdminUser>> {
  return request.get('/api/admin/v1/users', { params });
}

export function getUserDetail(id: string): Promise<AdminUser> {
  return request.get(`/api/admin/v1/users/${id}`);
}

export function getUserDetailFull(id: string): Promise<UserDetail> {
  return request.get(`/api/admin/v1/users/${id}/detail`);
}

export function getUserSessions(
  id: string,
  params?: { page?: number; pageSize?: number }
): Promise<PaginatedResponse<VoiceSession>> {
  return request.get(`/api/admin/v1/users/${id}/sessions`, { params });
}

export interface FoodLogItemResponse {
  id: number;
  name: string;
  foodItemId: number | null;
  amountText: string | null;
  estimatedGrams: number | null;
  calories: number | null;
  protein: number | null;
  fat: number | null;
  carbs: number | null;
  confidence: number | null;
  isUserEdited: boolean;
}

export interface FoodLogResponse {
  id: number;
  userId: number;
  voiceSessionId: number | null;
  mealType: string | null;
  logDate: string;
  originalText: string | null;
  totalCalories: number | null;
  totalProtein: number | null;
  totalFat: number | null;
  totalCarbs: number | null;
  source: string;
  items: FoodLogItemResponse[] | null;
  createdAt: number;
  updatedAt: number;
}

export function getUserFoodLogs(
  id: string,
  params?: { date?: string; page?: number; pageSize?: number }
): Promise<PaginatedResponse<FoodLogResponse>> {
  return request.get(`/api/admin/v1/users/${id}/food-logs`, { params });
}

export function updateUser(
  id: string,
  data: { nickname?: string; role?: string; status?: string }
): Promise<void> {
  return request.put(`/api/admin/v1/users/${id}`, data);
}
